import { setUnplToBridge } from "./bridge/setUnplToBridge";
import { Level, reportError } from "./owl";
import { getUnpl, requestUnplWithSessionId, UnplParams } from '@/api/unpl';
import { type ParsedBrowserEnv } from '@/lib/getBrowserEnv';
import { getUrlParams, getUrlParam } from '@/lib/urlParams';
import { setCookie } from '@/lib/cookie';
import { getUserId } from "./userInfo";
import { reportCustomMetric } from '@/lib/owl';
import { logInfo, reportLog } from './logan';
import { PrefetchData } from "@/types/serverSideProps";

let cachedAppEnv: ParsedBrowserEnv | null = null;

// 获取 unpl 相关状态
const unplStatus = {
  hasPTC: false, // URL 是否有 PTC 参数
  hasPrefetchUnpl: false, // 预取数据是否有 unpl
  hasStoreUnpl: false, // unpl 是否已经存储到 cookie 中
  bindSessionSuccess: false, // 是否 unpl 绑定 sessionId 成功
}

export function getUnplStatus() { return unplStatus };

/**
 * 通用上报错误
 * @param name 错误名称
 * @param error 错误信息
 * @param category  错误类型
 */
const reportUnplError = (name: string, error: any, category?: 'jsError' | 'customError' | 'resourceError' | 'ajaxError' | 'bridgeError' | 'baseError' | 'supplyError' | 'couponError') => {
  reportError({
    name,
    msg: error,
    category: category || 'jsError',
    level: Level.ERROR,
  });
}

type GetUnplStage =
  | 'getUnplFail'     // getUnpl 接口失败（请求异常或返回 unpl 为空）
  | 'bindSessionFail' // bindUnplWithSessionId 接口请求异常
  | 'success'         // 完整流程成功

/**
 * 自定义打点是否获取unpl成功
 */
const reportGetUnplResult = (stage: GetUnplStage) => {
  reportCustomMetric({
    key: 'getUnpl',
    value: stage === 'success' ? 1 : 0,
    tags: { stage },
  });
}

/**
 * 获取cookie参数
 * @returns 域名和path
 */
const getCookiOptions = () => {
  const host = window.location.host;
  const domain = host.includes('sankuai') ? '.sankuai.com' : '.meituan.com';
  return {
    domain,
    path: '/'
  }
}

/**
 * 在微信小程序下存储token
 */
const handleWxMiniProgramToken = (): void => {
  // 非小程序环境
  if (!cachedAppEnv?.isWXApp) {
    return;
  }
  const token = getUrlParam('token');
  if (token) {
    setCookie('oops', token, getCookiOptions());
  }
};

const bindUnplWithSessionId = async (sessionId: string, unpl: string) => {
  logInfo(`[bindUnplWithSessionId] start sessionId: ${sessionId}`);
  if (sessionId) {
    try {
      await requestUnplWithSessionId({
        session: sessionId || '',
        unpl
      });
      logInfo('[bindUnplWithSessionId] success');
      unplStatus.bindSessionSuccess = true;
    } catch (error) {
      reportGetUnplResult('bindSessionFail');
      reportUnplError('[requestUnplWithSessionId] request fail', error, 'ajaxError');
      logInfo(`[bindUnplWithSessionId] error: ${JSON.stringify(error)}`);
    } finally {
      reportLog();
    }
  }
}


/**
 * 处理默认 PTC 跟单逻辑
 */
const handleDefaultPtcTracking = async (
  tracker: any,
  setCompleteGetUnpl: any,
  _prefetchData?: PrefetchData
): Promise<void> => {
  try {
    const urlParams = getUrlParams() || {};

    // 若不存在ptc参数则不请求接口
    if (!urlParams.p || !urlParams.t || !urlParams.c) {
      logInfo(`[getUnpl] not ptc urlParams: ${JSON.stringify(urlParams)}`);
      return;
    }
    let sessionId = getUrlParam('msid') || window.fxSessionId || '';
    let sessionIdPromise: Promise<string> | null = null;
    setCompleteGetUnpl(false);

    logInfo(`[getUnpl] get sessionId start sessionId: ${sessionId}`);
    // 获取sessionId，不阻塞接口请求
    if (!sessionId) {
      sessionIdPromise = tracker?.getSessionId()
        .then((id: string) => {
          sessionId = id || '';
          return sessionId;
        }).catch(() => {
          sessionId = '';
          return '';
        });
    }

    logInfo('[getUnpl] UnplParams start');
    const params: UnplParams = {
      p: urlParams.p || '',
      t: urlParams.t || '',
      c: urlParams.c || '',
      utrans: urlParams.utrans || '',
      sessionId,
      referrer: window.document.referrer,
      // 优先从url上获取外投容器，然后从 _prefetchData 获取
      externalExp: urlParams.growth_strategy || urlParams.ffces || _prefetchData?.fx_strategy_key || '',
      externalStrategy: urlParams.growth_exp || urlParams.ffce || _prefetchData?.fx_external_exp || '',
      // 优先获取url上uuid和外投容器实验绑定，ssr实验和userid绑定
      uuid: urlParams.uuid || getUserId() || '',
    };

    logInfo(`[getUnpl] UnplParams end params: ${JSON.stringify(params)}`);

    handleWxMiniProgramToken();
    const { data: result } = await getUnpl(params);

    const unpl = result?.unpl || '';
    if (!unpl) {
      reportGetUnplResult('getUnplFail');
      reportUnplError('[getUnpl] fail get unpl', result);
      logInfo(`[getUnpl] fail get unpl ${JSON.stringify(result)}`);
    } else {
      logInfo('[getUnpl] success');
    }

    // 存储 unpl
    // H5环境下
    setCookie('unpl', unpl, getCookiOptions());
    // app环境内
    if (cachedAppEnv?.isMT) {
      logInfo('[getUnpl] tuanapp');
      setUnplToBridge(unpl, reportUnplError);
    }
    // 标记 unpl 已经存储到 cookie 中
    unplStatus.hasStoreUnpl = true;
    logInfo(`[getUnpl] start bind sessionId: ${params.sessionId}; unpl: ${unpl}; sessionIdPromise: ${Boolean(sessionIdPromise)}`);
    if (unpl) {
      if (params.sessionId) {
        // 请求 getUnpl 时已有 sessionId，接口内部已完成绑定
        unplStatus.bindSessionSuccess = true;
      } else if (sessionIdPromise) {
        // 请求时还没有 sessionId，等拿到后再绑定
        const resolvedSessionId = await sessionIdPromise;
        await bindUnplWithSessionId(resolvedSessionId, unpl);
      }
    }
    // unpl 写入且 sessionId 绑定完成
    reportGetUnplResult('success');
    setCompleteGetUnpl(true);
  } catch (error) {
    // bindUnplWithSessionId 内部已 catch，不会抛出，此处只会被 getUnpl 接口异常触发
    reportGetUnplResult('getUnplFail');
    setCompleteGetUnpl(true);
    reportUnplError('[getUnpl] request fail unpl', error);
    logInfo(`[getUnpl] request fail unpl ${JSON.stringify(error)}`);
  } finally {
    setTimeout(() => {
      reportLog();
    }, 3000);
  }
};

/**
 * 根据 PTC 换取 UNPL
 * 支持两种场景:
 * 1. 惠省小程序环境: 直接使用 URL 中的 unpl
 * 2. 默认环境: 通过 API 换取 unpl
 *
 * @param tracker - 跟踪器实例
 * @param setCompleteGetUnpl - 设置完成状态的回调函数
 * @param appEnv - 环境
 * @param _prefetchData - ssr预请求数据
 * @throws {Error} 当换取失败时抛出错误
 */
async function exchangePtcForUnpl(tracker: any, setCompleteGetUnpl: any, appEnv: ParsedBrowserEnv, _prefetchData?: PrefetchData): Promise<void> {
  cachedAppEnv = appEnv
  const urlParams = getUrlParams() || {};
  const unpl = _prefetchData?.unpl || '';

  // 初始化 UNPL 相关状态
  // 1. URL 是否有 PTC 参数
  unplStatus.hasPTC = Boolean(urlParams.p && urlParams.t && urlParams.c);
  // 2. 预取数据是否有 unpl
  unplStatus.hasPrefetchUnpl = Boolean(unpl);
  // 3. unpl 是否已经存储到 cookie 中
  // 4. 是否绑定 sessionId
  //    - CSR 场景：经过 click 中间页，无预取数据，不需要种 unpl & bind SessionId，直接置为 true
  //    - SSR 场景：有预取数据，需要种 unpl & bind SessionId，初始置为 false
  unplStatus.hasStoreUnpl = unplStatus.bindSessionSuccess = !_prefetchData;

  try {
    // ssr可以再全局获取unpl，无需请求
    if (unpl) {
      logInfo(`[getUnpl] _prefetchData get unpl success unpl: ${unpl}`);
      setCookie('unpl', unpl, getCookiOptions());
      if (cachedAppEnv?.isMT) {
        setUnplToBridge(unpl, reportUnplError);
      }
      // 标记 unpl 种 Cookie 完成
      unplStatus.hasStoreUnpl = true;
      setCompleteGetUnpl(false);
      // unpl和session绑定
      logInfo('[getUnpl] bindUnplWithSessionId start');
      const sessionId = window.fxSessionId || await tracker?.getSessionId();
      logInfo(`[getUnpl] getSessionId sessionId: ${sessionId}`);
      await bindUnplWithSessionId(sessionId, unpl);
      logInfo('[getUnpl] bindUnplWithSessionId end');
      reportGetUnplResult('success');
      setCompleteGetUnpl(true);
      return;
    }
    if (_prefetchData && !unpl) {
      logInfo(`[getUnpl] not get _prefetchData.unpl`);
    }
    // 请求点击接口获取unpl
    handleDefaultPtcTracking(tracker, setCompleteGetUnpl, _prefetchData);
  } catch (error) {
    reportGetUnplResult('bindSessionFail');
    setCompleteGetUnpl(true);
    reportUnplError('[getUnpl] exchangePtcForUnpl error', error);
    logInfo(`[getUnpl] exchangePtcForUnpl error ${JSON.stringify(error)}`);
  } finally {
    setTimeout(() => {
      reportLog();
    }, 3000);
  }
}

export default exchangePtcForUnpl;